import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import { setLogoutHandler } from "@/api/client"
import { fetchUserProfile } from "@/api/auth"
import {
  clearAuthToken,
  clearStoredProfile,
  getAuthToken,
  getStoredProfile,
  setAuthToken,
  setStoredProfile,
} from "@/lib/storage"

type AuthContextValue = {
  isAuthenticated: boolean
  isBootstrapping: boolean
  token: string | null
  profile: Record<string, unknown> | null
  login: (token: string) => Promise<void>
  logout: () => Promise<void>
  refreshProfile: () => Promise<Record<string, unknown> | null>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isBootstrapping, setIsBootstrapping] = useState(true)
  const [token, setToken] = useState<string | null>(() => getAuthToken())
  const [profile, setProfile] = useState<Record<string, unknown> | null>(() => {
    const stored = getStoredProfile()
    return stored && typeof stored === "object" && !Array.isArray(stored)
      ? (stored as Record<string, unknown>)
      : null
  })

  const logout = useCallback(async () => {
    clearAuthToken()
    clearStoredProfile()
    setToken(null)
    setProfile(null)
  }, [])

  const refreshProfile = useCallback(async () => {
    try {
      const data = await fetchUserProfile()
      if (data && typeof data === "object" && !Array.isArray(data)) {
        const next = data as Record<string, unknown>
        setStoredProfile(next)
        setProfile(next)
        return next
      }
      return null
    } catch {
      return null
    }
  }, [])

  const login = useCallback(
    async (nextToken: string) => {
      setAuthToken(nextToken)
      setToken(nextToken)
      await refreshProfile()
    },
    [refreshProfile]
  )

  useEffect(() => {
    setLogoutHandler(logout)
    return () => setLogoutHandler(null)
  }, [logout])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const existing = getAuthToken()
      if (!existing) {
        if (!cancelled) setIsBootstrapping(false)
        return
      }
      await refreshProfile()
      if (!cancelled) setIsBootstrapping(false)
    })()
    return () => {
      cancelled = true
    }
  }, [refreshProfile])

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: Boolean(token),
      isBootstrapping,
      token,
      profile,
      login,
      logout,
      refreshProfile,
    }),
    [isBootstrapping, token, profile, login, logout, refreshProfile]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within AuthProvider")
  return ctx
}
